package com.flowyride.core.domain.navigation

import com.flowyride.core.domain.analysis.bearingTo
import com.flowyride.core.domain.analysis.distanceTo
import com.flowyride.core.domain.model.GeoPoint
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.max
import kotlin.math.min

/**
 * A line with its distances precomputed: where is a point along it, how far from it.
 * Distances along the line are in meters from its first point.
 */
class TrackGeometry(val points: List<GeoPoint>) {

    /** Distance from the start at each point. */
    private val cumulative: DoubleArray = DoubleArray(points.size).also { distances ->
        for (i in 1 until points.size) distances[i] = distances[i - 1] + points[i - 1].distanceTo(points[i])
    }

    val length: Double get() = cumulative.lastOrNull() ?: 0.0

    fun distanceAtIndex(index: Int): Double = cumulative[index.coerceIn(0, points.lastIndex)]

    /** Index of the segment (its first point) that contains [distance]. */
    fun segmentAt(distance: Double): Int {
        if (points.size < 2) return 0
        var low = 0
        var high = points.size - 2
        while (low < high) {
            val mid = (low + high + 1) / 2
            if (cumulative[mid] <= distance) low = mid else high = mid - 1
        }
        return low
    }

    fun pointAt(distance: Double): GeoPoint {
        if (points.size < 2) return points.first()
        val d = distance.coerceIn(0.0, length)
        val i = segmentAt(d)
        val segment = cumulative[i + 1] - cumulative[i]
        val t = if (segment <= 0.0) 0.0 else (d - cumulative[i]) / segment
        val a = points[i]
        val b = points[i + 1]
        return GeoPoint(a.latitude + (b.latitude - a.latitude) * t, a.longitude + (b.longitude - a.longitude) * t)
    }

    /** Direction of the line at [distance], degrees within (-180, 180]. */
    fun bearingAt(distance: Double): Double {
        if (points.size < 2) return 0.0
        var i = segmentAt(distance.coerceIn(0.0, length))
        // Skip zero-length segments (repeated points).
        while (i < points.size - 2 && points[i] == points[i + 1]) i++
        return points[i].bearingTo(points[i + 1])
    }

    /**
     * @param distanceAlong where the closest point is, along the line.
     * @param offsetMeters how far [point] is from the line.
     */
    data class Projection(val distanceAlong: Double, val offsetMeters: Double, val bearing: Double)

    /**
     * Closest point of the line to [point], looking only at the part between [from] and [to]
     * (a lap that passes twice at the same place must not be confused with the other passage).
     *
     * @param score chooses between candidates; defaults to the plain distance.
     */
    fun project(
        point: GeoPoint,
        from: Double = 0.0,
        to: Double = length,
        score: (Projection) -> Double = { it.offsetMeters },
    ): Projection? {
        if (points.size < 2) return null
        val first = segmentAt(max(0.0, from))
        val last = segmentAt(min(length, to))
        val metersPerDegreeLat = EARTH_METERS_PER_DEGREE
        val metersPerDegreeLon = EARTH_METERS_PER_DEGREE * cos(point.latitude * PI / 180.0)
        var best: Projection? = null
        var bestScore = Double.MAX_VALUE
        for (i in first..last) {
            val a = points[i]
            val b = points[i + 1]
            // Local plane centred on the point: accurate enough over a few kilometres.
            val ax = (a.longitude - point.longitude) * metersPerDegreeLon
            val ay = (a.latitude - point.latitude) * metersPerDegreeLat
            val bx = (b.longitude - point.longitude) * metersPerDegreeLon
            val by = (b.latitude - point.latitude) * metersPerDegreeLat
            val dx = bx - ax
            val dy = by - ay
            val lengthSquared = dx * dx + dy * dy
            val t = if (lengthSquared <= 0.0) 0.0 else (-(ax * dx + ay * dy) / lengthSquared).coerceIn(0.0, 1.0)
            val px = ax + dx * t
            val py = ay + dy * t
            val offset = kotlin.math.sqrt(px * px + py * py)
            val along = cumulative[i] + (cumulative[i + 1] - cumulative[i]) * t
            if (along < from - 1.0 || along > to + 1.0) continue
            // Past the end of a segment, the line goes on in the next one's direction.
            val bearing = when {
                t >= 1.0 && i + 2 < points.size -> points[i + 1].bearingTo(points[i + 2])
                a == b -> 0.0
                else -> a.bearingTo(b)
            }
            val candidate = Projection(along, offset, bearing)
            val candidateScore = score(candidate)
            if (candidateScore < bestScore) {
                bestScore = candidateScore
                best = candidate
            }
        }
        return best
    }

    private companion object {
        const val EARTH_METERS_PER_DEGREE = 111_195.0
    }
}
